import * as React from "react";
import ReactDOM from "react-dom";

import { uuid } from "../../../../../../Common/UUID";

interface RenderInnerContainerProps extends RenderContainerProps {
    domContainer: null | HTMLElement;
    rootId: string;
}

const SSRPlaceholder = () => <script data-id="ssr-placeholder" />;

interface PortalProps {
    container: null | HTMLElement;
    rt_rootID: string;
    children: React.ReactNode;
}

export function Portal({ container, rt_rootID, children }: PortalProps) {
    return (
        <React.Fragment>
            {container ? ReactDOM.createPortal(children, container) : <SSRPlaceholder />}
            {container ? <noscript data-render-container-id={rt_rootID} /> : <SSRPlaceholder />}
        </React.Fragment>
    );
}

export class RenderInnerContainer extends React.Component<RenderInnerContainerProps> {
    public static __KONTUR_REACT_UI__ = "RenderInnerContainer";

    public render() {
        const { anchor, children, domContainer, rootId } = this.props;
        let inner = anchor;

        if (children) {
            inner = (
                <React.Fragment>
                    {anchor}
                    <Portal key="portal-ref" rt_rootID={rootId} container={domContainer}>
                        {children}
                    </Portal>
                </React.Fragment>
            );
        }

        return inner;
    }
}

const isBrowser = typeof window !== "undefined";
const canUseDOM = isBrowser && window.document && window.document.createElement != undefined;

export interface RenderContainerProps {
    anchor?: React.ReactNode;
    children?: React.ReactNode;
}

export class RenderContainer extends React.Component<RenderContainerProps> {
    public static __KONTUR_REACT_UI__ = "RenderContainer";

    private static readonly getRootId = () => uuid();
    private domContainer: null | HTMLElement = null;

    private readonly rootId: string = RenderContainer.getRootId();

    public constructor(props: RenderContainerProps) {
        super(props);
        if (isBrowser) {
            this.mountContainer();
        }
    }

    public UNSAFE_componentWillReceiveProps(nextProps: Readonly<RenderContainerProps>): void {
        if (!this.props.children && nextProps.children) {
            this.mountContainer();
        }
        if (this.props.children && !nextProps.children) {
            this.unmountContainer();
        }
    }

    public componentWillUnmount() {
        this.destroyContainer();
    }

    public render() {
        return <RenderInnerContainer {...this.props} domContainer={this.domContainer} rootId={this.rootId} />;
    }

    private createContainer() {
        if (canUseDOM) {
            const domContainer = document.createElement("div");
            domContainer.setAttribute("data-rendered-container-id", `${this.rootId}`);
            this.domContainer = domContainer;
        }
    }

    private mountContainer() {
        if (!this.domContainer) {
            this.createContainer();
        }
        if (this.domContainer && this.domContainer.parentNode !== document.body) {
            document.body.appendChild(this.domContainer);
        }
    }

    private destroyContainer() {
        if (this.domContainer) {
            this.unmountContainer();
            this.domContainer = null;
        }
    }

    private unmountContainer() {
        if (this.domContainer && this.domContainer.parentNode) {
            this.domContainer.parentNode.removeChild(this.domContainer);
        }
    }
}
